// src/components/groups/JoinRequestModal.jsx
// Modal for submitting a join request with optional message
// Used when group has auto_accept_members = false

import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { X } from 'lucide-react';
import Modal from '../core/Modal';
import { addBreadcrumb } from '../../utils/errorCapture';
import { JOIN_REQUEST_MESSAGE_MAX } from '../../utils/validationConstants';

const JoinRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  groupName,
  loading = false
}) => {
  const { t } = useTranslation('groups');
  const theme = useTheme();
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    addBreadcrumb('action', { action: 'join-request', component: 'JoinRequestModal' });
    onSubmit(message.trim() || null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!loading}
      closeOnBackdrop={() => !loading}
      panelStyle={{
        background: 'white',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '480px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}
    >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          background: theme?.primary || '#BB4D9A'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: 'white'
          }}>
            {t('join_request_title')}
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.5 : 1
            }}
          >
            <X size={24} color="white" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          <p style={{
            margin: '0 0 16px 0',
            fontSize: '15px',
            color: '#374151',
            lineHeight: '1.5'
          }}>
            {t('join_request_instructions')}
          </p>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              {t('group_label')}
            </label>
            <div style={{
              padding: '12px',
              background: '#f3f4f6',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              color: theme?.primary || '#BB4D9A'
            }}>
              {groupName || t('group_fallback')}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              {t('message_label')}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('message_placeholder')}
              disabled={loading}
              maxLength={JOIN_REQUEST_MESSAGE_MAX}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = theme?.primary || '#BB4D9A'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            <div style={{
              textAlign: 'right',
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '4px'
            }}>
              {message.length}/500
            </div>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1
              }}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                background: theme?.primary || '#BB4D9A',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? t('sending_button') : t('send_request_button')}
            </button>
          </div>

        </form>

    </Modal>
  );
};

export default JoinRequestModal;
